/**
 * Concert archive auth context — delegates to the shared AuthProvider.
 *
 * With Managed Login, the concert archive uses the same OAuth-only flow as
 * all other portals. All access now requires authentication (previously
 * read access was public).
 *
 * The `canEdit` property is computed from the user's group membership and
 * is exposed for UI-level gating of edit controls.
 */

import { createContext, useContext, type ReactNode } from 'react'
import {
  AuthProvider as SharedAuthProvider,
  useAuth as useSharedAuth,
  type AuthProviderConfig,
  type AuthContextType as SharedAuthContextType,
} from '@brent/shared'

const EDITOR_GROUP = 'concert-archive-editor'

// ── Extended context with canEdit ─────────────────────────────────────────────

interface ConcertArchiveAuthContextType extends SharedAuthContextType {
  /** Whether the authenticated user can edit concert archive data */
  canEdit: boolean
  /** Sign in via Managed Login (alias for login) */
  signIn: () => Promise<void>
  /** Sign out (alias for logout) */
  signOut: () => void
}

const ConcertArchiveAuthContext = createContext<ConcertArchiveAuthContextType | undefined>(
  undefined,
)

export function useAuth(): ConcertArchiveAuthContextType {
  const context = useContext(ConcertArchiveAuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// ── Provider ──────────────────────────────────────────────────────────────────

const concertArchiveConfig: AuthProviderConfig = {
  userPoolId: import.meta.env.VITE_CA_COGNITO_USER_POOL_ID,
  clientId: import.meta.env.VITE_CA_COGNITO_CLIENT_ID,
  region: import.meta.env.VITE_CA_COGNITO_REGION || 'eu-west-1',
  domain: import.meta.env.VITE_CA_COGNITO_DOMAIN,
  redirectUri: import.meta.env.VITE_CA_COGNITO_REDIRECT_URI,
  logoutUri: import.meta.env.VITE_CA_COGNITO_LOGOUT_URI ?? window.location.origin,
}

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Inner provider that wraps the shared auth context and adds `canEdit`.
 */
function ConcertArchiveAuthBridge({ children }: { children: ReactNode }) {
  const sharedAuth = useSharedAuth()

  const value: ConcertArchiveAuthContextType = {
    ...sharedAuth,
    canEdit: !!sharedAuth.user && sharedAuth.user.groups.includes(EDITOR_GROUP),
    // Provide aliases for backward compatibility with existing concert-archive components
    signIn: sharedAuth.login,
    signOut: sharedAuth.logout,
  }

  return (
    <ConcertArchiveAuthContext.Provider value={value}>
      {children}
    </ConcertArchiveAuthContext.Provider>
  )
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <SharedAuthProvider config={concertArchiveConfig}>
      <ConcertArchiveAuthBridge>{children}</ConcertArchiveAuthBridge>
    </SharedAuthProvider>
  )
}
